/**
 * Returns a function that wraps async tasks so at most `limit` run concurrently.
 * Usage: const run = createSemaphore(5);
 *        await Promise.all(items.map(item => run(() => doWork(item))));
 */
export function createSemaphore(limit) {
	let active = 0;
	const queue = [];
	return (fn) =>
		new Promise((resolve, reject) => {
			const run = () => {
				active++;
				fn()
					.then(resolve, reject)
					.finally(() => {
						active--;
						if (queue.length) queue.shift()();
					});
			};
			active < limit ? run() : queue.push(run);
		});
}

/**
 * Fetches a URL with automatic retry on failure.
 * @param {string} url
 * @param {object} [options]
 * @param {number} [options.retries=2]      Max retry attempts after first failure
 * @param {number} [options.backoffMs=1000] Base delay; multiplied by attempt number
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(url, { retries = 2, backoffMs = 1000 } = {}) {
	for (let attempt = 0; attempt <= retries; attempt++) {
		try {
			const res = await fetch(url);
			if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
			return res;
		} catch (err) {
			if (attempt === retries) throw err;
			await new Promise((r) => setTimeout(r, backoffMs * (attempt + 1)));
		}
	}
}
